import { useEffect, useRef, useState } from 'preact/hooks';
import { loadItem } from '../data';
import { Pinned } from './Chrome';
import { go, To } from './TrustPages';
import './report.css';

// Report an error (README section 8), at /report or /report?item=<id> from an item's pop-up.
//
// NOTHING A READER SENDS IS APPLIED AS SENT (Dan, 21 Sep 2026): every report is read and
// checked against the official record first, and only a fix the record supports is made —
// then dated in the corrections log. The page says so before the form, and again after.
//
// Sending goes to the site's own server (Phase 3), which is the only thing that can write
// error_reports; the browser holds no database key. THE CONTRACT THAT SERVER MUST KEEP:
//   POST /api/report, JSON {item_id: string|null, message: string, website: string}
//   · item_id  free text, at most 200 characters (the table keeps a bad id rather than lose it)
//   · message  4 to 4000 characters, the table's own check
//   · website  the honeypot: a person never fills it; the server drops the report and still
//              answers as if it were accepted, so a bot learns nothing
//   → 2xx accepted · 400 refused as invalid · 429 too many from this network just now
//   · 503 up but cannot take it now (reports switched off, the overall cap, or the store failed),
//         with the reason in `why`
// server/srweb/reports.py and app.py keep it (21 Sep 2026). A send that fails SAYS SO, and
// nothing is stored anywhere.

const ENDPOINT = '/api/report';
const ITEM_ID = /^[a-z0-9-]+$/;

type Status = { k: 'editing' } | { k: 'sending' } | { k: 'sent' } | { k: 'failed'; why: string };

export function ReportPage() {
  const [item, setItem] = useState('');
  const [message, setMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState<Status>({ k: 'editing' });
  // From an item's pop-up: its id, and the words the field was filled with. The exact id is
  // sent while the field still says what it was filled with, so the desk can open that item.
  const [prefill, setPrefill] = useState<{ id: string; text: string } | null>(null);
  // The item's name arrives a moment after the page. If the reader has already typed in the
  // field by then, what they typed stands.
  const touched = useRef(false);

  useEffect(() => {
    const id = new URLSearchParams(location.search).get('item');
    if (!id || !ITEM_ID.test(id)) return;
    let live = true;
    const fill = (text: string) => { if (live && !touched.current) { setPrefill({ id, text }); setItem(text); } };
    loadItem(id)
      .then((doc) => fill(doc.title ? `${doc.number} — ${doc.title}` : doc.number))
      .catch(() => fill(id));
    return () => { live = false; };
  }, []);

  const ready = message.trim().length > 3 && status.k !== 'sending';

  const send = async () => {
    if (!ready) return;
    const typed = item.trim();
    const itemId = prefill && item === prefill.text ? prefill.id : typed || null;
    setStatus({ k: 'sending' });
    try {
      const r = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: itemId, message: message.trim(), website: honeypot }),
      });
      if (r.ok) { setStatus({ k: 'sent' }); return; }
      // 503: the server is up but could not take it (switched off, busy, or the store failed),
      // and it says which in `why`.
      let why = '';
      try { why = String((await r.json()).why ?? ''); } catch { /* no reason given */ }
      if (r.status === 503 && why) {
        setStatus({ k: 'failed', why: `The site’s server could not take it just now: ${why}.` });
        return;
      }
      setStatus({
        k: 'failed',
        why: r.status === 429
          ? 'Too many reports have come from your network in the last few minutes. Please try again a little later.'
          : r.status === 400
            ? 'The server could not accept it as written (400). Check that the message says what is wrong, in under 4,000 characters.'
            : `The site’s server did not accept it (${r.status}).`,
      });
    } catch {
      setStatus({ k: 'failed', why: 'The site’s server could not be reached.' });
    }
  };

  const again = () => { setStatus({ k: 'editing' }); setItem(''); setMessage(''); setPrefill(null); };

  return (
    <Pinned class="sr-report-wrap">
      <div class="sr-report">
        <header class="sr-report-head">
          <div class="sr-report-kicker">Corrections</div>
          <h2>Report an error</h2>
          <button class="btn btn-ghost sr-report-back" onClick={() => go('/')}>← Back to the record</button>
        </header>

        {status.k === 'sent' ? (
          <div class="sr-report-body">
            <h3>Received.</h3>
            <p class="sr-report-p">Nothing about you was stored with it: no name, no address, no IP address.</p>
            <p class="sr-report-p">It will be checked against the official record before anything changes. If the
              record shows the site is wrong, the fix appears in the corrections log with the date and what changed.
              If the record shows the site is right, nothing changes. There is no reply, because the form never
              asked who you are.</p>
            <div class="sr-report-actions">
              <button class="btn btn-secondary" onClick={again}>Report another</button>
              <button class="btn btn-secondary" onClick={() => go('/corrections')}>Read the corrections log</button>
            </div>
          </div>
        ) : (
          <div class="sr-report-body">
            <p class="sr-report-p">If a vote, a tally, a date, a name or a summary line does not match the official
              record, say so here.</p>
            <div class="sr-report-check">
              <div class="sr-report-check-label">What happens next</div>
              <p>Every report is read and checked against the official record before anything changes. Nothing is
                applied as it arrives. If the record shows the site is wrong, the fix is made, dated and listed in
                the <To path="/corrections">corrections log</To>; if the record shows the site is right, nothing
                changes. There is no reply, because the form does not ask who you are.</p>
            </div>

            <label class="sr-report-label" for="sr-report-item">Which item or member</label>
            <input id="sr-report-item" class="input sr-report-input" value={item} maxLength={200}
                   onInput={(e) => { touched.current = true; setItem((e.target as HTMLInputElement).value); }}
                   placeholder="e.g. H.R. 7148, or a member’s name" />

            <label class="sr-report-label sr-report-gap" for="sr-report-msg">What is wrong with it</label>
            <textarea id="sr-report-msg" class="input sr-report-input" rows={5} value={message} maxLength={4000}
                      onInput={(e) => setMessage((e.target as HTMLTextAreaElement).value)}
                      placeholder="What the site shows, and what the official record shows." />
            <div class="sr-report-note">Please do not include your name, address, or anything else personal.
              Whatever you type here is kept.</div>

            {/* The honeypot: hidden from people and from screen readers, never reached by Tab. */}
            <div class="sr-report-trap" aria-hidden="true">
              <label>Leave this field empty
                <input value={honeypot} tabIndex={-1} autoComplete="off"
                       onInput={(e) => setHoneypot((e.target as HTMLInputElement).value)} />
              </label>
            </div>

            {status.k === 'failed' && (
              <div class="sr-report-failed" role="alert">
                <strong>Not sent.</strong> {status.why} Nothing was stored. Your text is still here, so you can
                try again.
              </div>
            )}

            <div class="sr-report-actions">
              <button class="btn btn-primary" disabled={!ready} onClick={send}>
                {status.k === 'sending' ? 'Sending…' : 'Send report'}
              </button>
              <span class="sr-report-note">No account, no email address, no IP address stored.</span>
            </div>
          </div>
        )}
      </div>
    </Pinned>
  );
}
